import api from './api';

export type LibraryItemType = 'course' | 'lesson';
export type LibraryAccessType = 'readonly' | 'copy_allowed';
export type LibraryReferenceMode = 'copy' | 'reference';

export interface LibraryItemAccess {
    type: LibraryAccessType;
    canCopy: boolean;
    expiresAt: string | null;
}

export interface LibraryItem {
    id: number;
    uuid: string;
    itemType: LibraryItemType;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    tags: string[] | null;
    sourceType: string;
    isOperatorContent: boolean;
    version: number;
    usageCount: number;
    createdAt: string;
    updatedAt: string;
    access: LibraryItemAccess;
    contentSnapshot?: ContentSnapshot;
}

export interface ContentSnapshot {
    type: string;
    snapshotVersion: number;
    createdAt: string;
    course?: CourseSnapshot;
    lesson?: LessonSnapshot;
    lessons?: { lesson: LessonSnapshot }[];
}

export interface CourseSnapshot {
    title: string;
    slug: string;
    description: string | null;
    thumbnailUrl: string | null;
    estimatedDuration: number | null;
    difficulty: string;
    requireSequentialCompletion: boolean;
    passingScore: number;
    tags: string[] | null;
}

export interface LessonSnapshot {
    title: string;
    type: string;
    description: string | null;
    content: Record<string, unknown> | null;
    sortOrder: number;
    estimatedDuration: number | null;
    isMandatory: boolean;
    minScore: number | null;
    hasPeerReview: boolean;
    peerReviewCriteria: unknown | null;
    minPeerReviews: number;
}

export interface LibraryListResponse {
    items: LibraryItem[];
    total: number;
}

export interface LibraryAccessGrant {
    id: number;
    companyId: number;
    accessType: LibraryAccessType;
    canCopy: boolean;
    expiresAt: string | null;
    isExpired: boolean;
    price: string | null;
    currency: string | null;
    grantedBy: number | null;
    createdAt: string;
}

export interface LibraryAccessListResponse {
    accessGrants: LibraryAccessGrant[];
    total: number;
}

export interface LibraryReference {
    id: number;
    targetType: string;
    targetId: number;
    mode: LibraryReferenceMode;
    sourceVersionAtCreation: number;
    currentVersion: number | null;
    hasUpdate: boolean;
    libraryItem: {
        id: number;
        title: string;
        itemType: LibraryItemType;
    } | null;
    createdAt: string;
}

export interface UpdatesCheckResponse {
    updates: LibraryReference[];
    total: number;
}

export interface SaveToLibraryData {
    title?: string;
    description?: string;
    tags?: string[];
    isOperatorContent?: boolean;
}

export interface InsertCourseData {
    catalogId: number;
    mode: LibraryReferenceMode;
}

export interface InsertLessonData {
    courseId: number;
    mode: LibraryReferenceMode;
}

export interface ShareLibraryItemData {
    companyId: number;
    accessType: LibraryAccessType;
    expiresAt?: string;
    price?: string;
    currency?: string;
}

export interface Company {
    id: number;
    name: string;
}

export interface CompanyListResponse {
    companies: Company[];
    total: number;
}

export interface InsertCourseResponse {
    success: boolean;
    course: {
        id: number;
        title: string;
        slug: string;
    };
    mode: LibraryReferenceMode;
}

export interface InsertLessonResponse {
    success: boolean;
    lesson: {
        id: number;
        title: string;
    };
    mode: LibraryReferenceMode;
}

class LibraryService {
    /**
     * Get all accessible library items
     */
    async getLibraryItems(params?: {
        type?: LibraryItemType;
        search?: string;
        operatorOnly?: boolean;
    }): Promise<LibraryItem[]> {
        const searchParams = new URLSearchParams();
        if (params?.type) searchParams.append('type', params.type);
        if (params?.search) searchParams.append('search', params.search);
        if (params?.operatorOnly) searchParams.append('operatorOnly', 'true');

        const query = searchParams.toString();
        const response = await api.get<LibraryListResponse>(
            `/library${query ? `?${query}` : ''}`
        );
        return response.data.items;
    }

    /**
     * Get a single library item by ID
     */
    async getLibraryItem(id: number): Promise<LibraryItem> {
        const response = await api.get<LibraryItem>(`/library/${id}`);
        return response.data;
    }

    /**
     * Save a course to the library
     */
    async saveCourseToLibrary(courseId: number, data: SaveToLibraryData): Promise<LibraryItem> {
        const response = await api.post<LibraryItem>(`/library/save-course/${courseId}`, data);
        return response.data;
    }

    /**
     * Save a lesson to the library
     */
    async saveLessonToLibrary(lessonId: number, data: SaveToLibraryData): Promise<LibraryItem> {
        const response = await api.post<LibraryItem>(`/library/save-lesson/${lessonId}`, data);
        return response.data;
    }

    /**
     * Insert a course from the library
     */
    async insertCourseFromLibrary(
        libraryItemId: number,
        data: InsertCourseData
    ): Promise<InsertCourseResponse> {
        const response = await api.post<InsertCourseResponse>(
            `/library/insert-course/${libraryItemId}`,
            data
        );
        return response.data;
    }

    /**
     * Insert a lesson from the library
     */
    async insertLessonFromLibrary(
        libraryItemId: number,
        data: InsertLessonData
    ): Promise<InsertLessonResponse> {
        const response = await api.post<InsertLessonResponse>(
            `/library/insert-lesson/${libraryItemId}`,
            data
        );
        return response.data;
    }

    /**
     * Share a library item with a company (operator only)
     */
    async shareLibraryItem(libraryItemId: number, data: ShareLibraryItemData): Promise<LibraryAccessGrant> {
        const response = await api.post<LibraryAccessGrant>(`/library/share/${libraryItemId}`, data);
        return response.data;
    }

    /**
     * Revoke library item access from a company (operator only)
     */
    async revokeLibraryAccess(libraryItemId: number, companyId: number): Promise<void> {
        await api.delete(`/library/revoke/${libraryItemId}/${companyId}`);
    }

    /**
     * Get access grants for a library item (operator only)
     */
    async getLibraryItemAccess(libraryItemId: number): Promise<LibraryAccessGrant[]> {
        const response = await api.get<LibraryAccessListResponse>(`/library/${libraryItemId}/access`);
        return response.data.accessGrants;
    }

    /**
     * Check for available updates on referenced content
     */
    async checkForUpdates(): Promise<LibraryReference[]> {
        const response = await api.get<UpdatesCheckResponse>('/library/check-updates');
        return response.data.updates;
    }

    /**
     * Apply an update from the library to referenced content
     */
    async applyUpdate(referenceId: number): Promise<void> {
        await api.post(`/library/apply-update/${referenceId}`);
    }

    /**
     * Delete a library item
     */
    async deleteLibraryItem(id: number): Promise<void> {
        await api.delete(`/library/${id}`);
    }

    /**
     * Get list of companies for sharing (operator only)
     */
    async getCompanies(): Promise<Company[]> {
        const response = await api.get<CompanyListResponse>('/library/companies');
        return response.data.companies;
    }

    /**
     * Update library item metadata
     */
    async updateLibraryItem(
        id: number,
        data: Partial<Pick<LibraryItem, 'title' | 'description' | 'tags' | 'thumbnailUrl'>>
    ): Promise<LibraryItem> {
        const response = await api.patch<LibraryItem>(`/library/${id}`, data);
        return response.data;
    }
}

export const libraryService = new LibraryService();
export default libraryService;
